/**
 * JSON-LD builders.
 *
 * These produce the machine-readable "card" that Google, Bing and AI answer
 * engines read alongside the page. Types are chosen for what search engines
 * actually consume: ProfilePage/Person for DReps, Article for proposals
 * (co-typed as Legislation so semantic consumers get the accurate term),
 * BreadcrumbList everywhere, and ItemList on directory pages.
 */

import { absoluteUrl, siteUrl } from './config';
import type { DRepSeoRecord } from './catalog';
import {
  deriveProposalState,
  lovelaceToAdaLabel,
  proposalFacts,
  proposalTypeLabel,
  stateStamp,
  truncate,
  type IndexedProposal,
} from './proposal';
import { avatarUrl } from './image';

type Json = Record<string, unknown>;

const ORGANIZATION_ID = `${siteUrl}/#organization`;
const WEBSITE_ID = `${siteUrl}/#website`;

/** Drop null/undefined/empty members so the emitted JSON-LD stays clean. */
function compact<T extends Json>(input: T): T {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  ) as T;
}

function referenceUrls(references: unknown): string[] {
  if (!Array.isArray(references)) return [];
  const urls = references
    .map((reference) =>
      typeof reference === 'object' && reference !== null
        ? String((reference as { uri?: unknown }).uri ?? '')
        : ''
    )
    .filter((uri) => /^https?:\/\//.test(uri));
  return Array.from(new Set(urls));
}

export function organizationSchema(): Json {
  return compact({
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'DRep ID',
    alternateName: 'DRep.iD',
    url: `${siteUrl}/`,
    logo: absoluteUrl('/banner.png'),
    description:
      'DRep ID publishes Cardano delegated representative profiles and governance action results, so ADA holders can research who represents them before delegating their voting power.',
    sameAs: [
      'https://x.com/SiretoTech',
      'https://www.linkedin.com/company/sireto-technology/',
    ],
    // The support address the homepage FAQ already prints. Deliberately not
    // `LocalBusiness` with an `address`: this is a web service with no
    // premises to visit, and claiming a storefront to satisfy a generic audit
    // recommendation would be a false statement in structured data.
    email: 'support@drep.id',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@drep.id',
      availableLanguage: 'en',
    },
  });
}

export function websiteSchema(): Json {
  return compact({
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: `${siteUrl}/`,
    name: 'DRep ID',
    description:
      'Directory of Cardano DReps and governance actions, with voting records, voting power and delegation tools.',
    publisher: { '@id': ORGANIZATION_ID },
    inLanguage: 'en',
    // No `potentialAction`/`SearchAction`. It advertised
    // `/dreps?query={search_term_string}`, but `/dreps` reads only `page` and
    // ignores `query`, so every sitelinks-search-box query Google built from
    // this landed on an unfiltered page one. Reinstate it only together with
    // real server-side filtering on that route, and give the filtered variants
    // a canonical pointing at `/dreps` so they cannot be indexed separately.
  });
}

export function breadcrumbSchema(
  trail: Array<{ name: string; path: string }>
): Json {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(crumb.path),
    })),
  };
}

export function drepDisplayName(record: DRepSeoRecord): string {
  return (
    record.givenName?.trim() ||
    record.declaredName?.trim() ||
    `DRep ${record.drepId.slice(0, 12)}…`
  );
}

/** Best available prose for a DRep, in the order a reader would want it. */
export function drepSummary(record: DRepSeoRecord): string | null {
  const source =
    record.objectives ||
    record.bio ||
    record.motivations ||
    record.qualifications;
  return source ? truncate(source, 300) : null;
}

export function drepProfileSchema(record: DRepSeoRecord, path: string): Json {
  const name = drepDisplayName(record);
  const summary = drepSummary(record);
  const url = absoluteUrl(path);

  // Script-based DReps are multi-signature entities — DAOs, foundations,
  // community groups — rather than individuals.
  const entity = compact({
    '@type': record.isScript ? 'Organization' : 'Person',
    '@id': `${url}#drep`,
    name,
    // A second *name*, not a handle: `declaredName` is self-declared, so it
    // must never be presented as an owned identifier.
    alternateName:
      record.declaredName && record.declaredName !== name
        ? record.declaredName
        : undefined,
    description: summary,
    // Absolute and crawlable: Google will not use a relative or data: value
    // for rich results. `/drep-avatar/<id>` covers the data-URI DReps.
    image: (() => {
      const resolved = avatarUrl(record);
      return resolved ? absoluteUrl(resolved) : undefined;
    })(),
    url,
    identifier: record.drepId,
    sameAs: referenceUrls(record.references),
    knowsAbout: ['Cardano', 'Blockchain governance', 'CIP-1694'],
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'DRep ID (CIP-129)',
        value: record.drepId,
      },
      record.drepIdCip105
        ? {
            '@type': 'PropertyValue',
            name: 'DRep ID (CIP-105)',
            value: record.drepIdCip105,
          }
        : null,
    ].filter(Boolean),
  });

  return compact({
    '@type': 'ProfilePage',
    '@id': `${url}#profile`,
    url,
    name: `${name} — Cardano DRep`,
    description: summary,
    dateModified: record.updatedAt || undefined,
    isPartOf: { '@id': WEBSITE_ID },
    mainEntity: entity,
    breadcrumb: breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'DReps', path: '/dreps' },
      { name, path },
    ]),
  });
}

export function proposalSchema(proposal: IndexedProposal, path: string): Json {
  const state = deriveProposalState(proposal);
  const stamp = stateStamp(proposal);
  const url = absoluteUrl(path);
  const title =
    proposal.meta?.title?.trim() ||
    `${proposalTypeLabel(proposal.proposal?.type)} ${proposal.id.slice(0, 16)}…`;
  const amount = lovelaceToAdaLabel(
    (proposal.proposal?.details as Record<string, unknown>)?.['Amount']
  );

  return compact({
    // Article is what search engines consume; Legislation is the accurate
    // vocabulary term for a governance action put to a vote.
    '@type': ['Article', 'Legislation'],
    '@id': `${url}#proposal`,
    url,
    headline: truncate(title, 110),
    name: title,
    description: proposal.meta?.abstract
      ? truncate(proposal.meta.abstract, 300)
      : proposalFacts(proposal),
    abstract: proposal.meta?.abstract || undefined,
    datePublished: proposal.createdAt?.time || undefined,
    dateModified: stamp?.time || proposal.createdAt?.time || undefined,
    legislationIdentifier: proposal.id,
    legislationType: proposalTypeLabel(proposal.proposal?.type),
    legislationDate: proposal.createdAt?.time || undefined,
    legislationLegalForce: state === 'enacted' ? 'InForce' : 'NotInForce',
    creditText: 'Cardano on-chain governance',
    isPartOf: { '@id': WEBSITE_ID },
    publisher: { '@id': ORGANIZATION_ID },
    about: {
      '@type': 'Thing',
      name: 'Cardano governance',
    },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Status',
        value: state,
      },
      {
        '@type': 'PropertyValue',
        name: 'Action type',
        value: proposalTypeLabel(proposal.proposal?.type),
      },
      amount
        ? {
            '@type': 'PropertyValue',
            name: 'Requested amount',
            value: amount,
          }
        : null,
      proposal.createdAt?.epoch
        ? {
            '@type': 'PropertyValue',
            name: 'Submitted in epoch',
            value: String(proposal.createdAt.epoch),
          }
        : null,
      stamp?.epoch
        ? {
            '@type': 'PropertyValue',
            name: `${state[0].toUpperCase()}${state.slice(1)} in epoch`,
            value: String(stamp.epoch),
          }
        : null,
    ].filter(Boolean),
    breadcrumb: breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Governance actions', path: '/governance_actions' },
      { name: truncate(title, 60), path },
    ]),
  });
}

export function itemListSchema(
  items: Array<{ name: string; path: string }>,
  listName: string
): Json {
  return {
    '@type': 'ItemList',
    name: listName,
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      url: absoluteUrl(item.path),
    })),
  };
}

/** Wrap one or more schema objects into a single @graph document. */
export function graph(...nodes: Array<Json | null | undefined>): Json {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes.filter(Boolean),
  };
}

export function faqSchema(
  questions: Array<{ question: string; answer: string }>
): Json {
  return {
    '@type': 'FAQPage',
    mainEntity: questions.map((entry) => ({
      '@type': 'Question',
      name: entry.question,
      acceptedAnswer: { '@type': 'Answer', text: entry.answer },
    })),
  };
}
